import React, { useMemo } from 'react';
import { formatDatumIso } from '../utils/casUtils';
import {
    Box,
    Paper,
    Typography,
    ToggleButton,
    ToggleButtonGroup,
    useTheme,
} from '@mui/material';
import {
    BarChart,
    Bar,
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    Legend,
    PieChart,
    Pie,
    Cell,
} from 'recharts';
import type { StatistikaPoplach } from '../api/JsdhNotifikaceService/generated';

export type ChartView = 'trend' | 'typy' | 'odpovedi';

const ALL_VIEWS: ChartView[] = ['trend', 'typy', 'odpovedi'];

interface TrendChartProps {
    poplachy?: StatistikaPoplach[];
    podleTypu?: Record<string, number>;
    allowedViews?: ChartView[];
}

interface TrendDataPoint {
    datum: string;
    pocetPoplachu: number;
    pocetJedu: number;
    pocetNejedu: number;
}

const COLORS = ['#4caf50', '#f44336', '#2196f3', '#ff9800', '#9c27b0', '#00bcd4'];

const TrendChart: React.FC<TrendChartProps> = ({ poplachy = [], podleTypu, allowedViews }) => {
    const theme = useTheme();
    const effectiveViews = allowedViews ?? ALL_VIEWS;
    const [view, setView] = React.useState<ChartView>(effectiveViews[0] ?? 'trend');

    // Agregace dat podle týdnů pro trend
    const trendData = useMemo((): TrendDataPoint[] => {
        if (!poplachy || poplachy.length === 0) return [];

        const groupedByWeek = new Map<string, TrendDataPoint>();

        poplachy.forEach((poplach) => {
            if (!poplach.casVyhlaseni) return;

            const date = new Date(poplach.casVyhlaseni);
            // Získat pondělí daného týdne
            const dayOfWeek = date.getDay();
            const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
            const monday = new Date(date);
            monday.setDate(date.getDate() + mondayOffset);
            const weekKey = formatDatumIso(monday);

            const existing = groupedByWeek.get(weekKey) || {
                datum: weekKey,
                pocetPoplachu: 0,
                pocetJedu: 0,
                pocetNejedu: 0,
            };

            existing.pocetPoplachu += 1;
            existing.pocetJedu += poplach.pocetJedu || 0;
            existing.pocetNejedu += poplach.pocetNejedu || 0;

            groupedByWeek.set(weekKey, existing);
        });

        return Array.from(groupedByWeek.values())
            .sort((a, b) => a.datum.localeCompare(b.datum))
            .map((item) => ({
                ...item,
                datum: formatWeekLabel(item.datum),
            }));
    }, [poplachy]);

    // Data pro graf typů událostí
    const typyData = useMemo(() => {
        if (!podleTypu) return [];

        return Object.entries(podleTypu)
            .map(([typ, pocet]) => ({
                name: typ || 'Neznámý',
                value: pocet,
            }))
            .sort((a, b) => b.value - a.value)
            .slice(0, 6);
    }, [podleTypu]);

    // Data pro pie chart odpovědí
    const odpovediData = useMemo(() => {
        const totalJedu = poplachy.reduce((sum, p) => sum + (p.pocetJedu || 0), 0);
        const totalNejedu = poplachy.reduce((sum, p) => sum + (p.pocetNejedu || 0), 0);

        return [
            { name: 'JEDU', value: totalJedu, color: theme.palette.success.main },
            { name: 'NEJEDU', value: totalNejedu, color: theme.palette.error.main },
        ];
    }, [poplachy, theme]);

    const handleViewChange = (
        _event: React.MouseEvent<HTMLElement>,
        newView: ChartView | null
    ) => {
        if (newView) {
            setView(newView);
        }
    };

    const noData = poplachy.length === 0;

    return (
        <Paper sx={{ p: 2 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">
                    {view === 'trend' && 'Trend poplachů'}
                    {view === 'typy' && 'Poplachy podle typu'}
                    {view === 'odpovedi' && 'Odpovědi celkem'}
                </Typography>

                <ToggleButtonGroup
                    value={view}
                    exclusive
                    onChange={handleViewChange}
                    size="small"
                >
                    {effectiveViews.includes('trend') && (
                        <ToggleButton value="trend">Trend</ToggleButton>
                    )}
                    {effectiveViews.includes('typy') && (
                        <ToggleButton value="typy" disabled={!podleTypu}>Typy</ToggleButton>
                    )}
                    {effectiveViews.includes('odpovedi') && (
                        <ToggleButton value="odpovedi">Odpovědi</ToggleButton>
                    )}
                </ToggleButtonGroup>
            </Box>

            {noData ? (
                <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    height={300}
                    sx={{ opacity: 0.5 }}
                >
                    <Typography color="text.secondary">
                        Žádná data k zobrazení
                    </Typography>
                </Box>
            ) : (
                <Box height={300}>
                    {view === 'trend' && (
                        <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={trendData}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis
                                    dataKey="datum"
                                    fontSize={12}
                                    tick={{ fill: theme.palette.text.secondary }}
                                />
                                <YAxis
                                    fontSize={12}
                                    tick={{ fill: theme.palette.text.secondary }}
                                />
                                <Tooltip
                                    contentStyle={{
                                        backgroundColor: theme.palette.background.paper,
                                        borderColor: theme.palette.divider,
                                    }}
                                />
                                <Legend />
                                <Line
                                    type="monotone"
                                    dataKey="pocetPoplachu"
                                    name="Poplachy"
                                    stroke={theme.palette.primary.main}
                                    strokeWidth={2}
                                    dot={{ fill: theme.palette.primary.main }}
                                />
                                <Line
                                    type="monotone"
                                    dataKey="pocetJedu"
                                    name="JEDU"
                                    stroke={theme.palette.success.main}
                                    strokeWidth={2}
                                    dot={{ fill: theme.palette.success.main }}
                                />
                                <Line
                                    type="monotone"
                                    dataKey="pocetNejedu"
                                    name="NEJEDU"
                                    stroke={theme.palette.error.main}
                                    strokeWidth={2}
                                    dot={{ fill: theme.palette.error.main }}
                                />
                            </LineChart>
                        </ResponsiveContainer>
                    )}

                    {view === 'typy' && typyData.length > 0 && (
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={typyData} layout="vertical">
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis
                                    type="number"
                                    fontSize={12}
                                    tick={{ fill: theme.palette.text.secondary }}
                                />
                                <YAxis
                                    type="category"
                                    dataKey="name"
                                    fontSize={12}
                                    width={120}
                                    tick={{ fill: theme.palette.text.secondary }}
                                />
                                <Tooltip
                                    contentStyle={{
                                        backgroundColor: theme.palette.background.paper,
                                        borderColor: theme.palette.divider,
                                    }}
                                />
                                <Bar dataKey="value" name="Počet" radius={[0, 4, 4, 0]}>
                                    {typyData.map((entry, index) => (
                                        <Cell
                                            key={`cell-${index}`}
                                            fill={COLORS[index % COLORS.length]}
                                        />
                                    ))}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    )}

                    {view === 'odpovedi' && (
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Pie
                                    data={odpovediData}
                                    cx="50%"
                                    cy="50%"
                                    innerRadius={60}
                                    outerRadius={100}
                                    paddingAngle={2}
                                    dataKey="value"
                                    label={({ name, percent }) =>
                                        `${name} (${((percent ?? 0) * 100).toFixed(0)}%)`
                                    }
                                >
                                    {odpovediData.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={entry.color} />
                                    ))}
                                </Pie>
                                <Tooltip
                                    contentStyle={{
                                        backgroundColor: theme.palette.background.paper,
                                        borderColor: theme.palette.divider,
                                    }}
                                />
                                <Legend />
                            </PieChart>
                        </ResponsiveContainer>
                    )}
                </Box>
            )}
        </Paper>
    );
};

function formatWeekLabel(dateStr: string): string {
    const date = new Date(dateStr);
    const day = date.getDate();
    const month = date.getMonth() + 1;
    return `${day}.${month}.`;
}

export default React.memo(TrendChart);
